import { create } from 'zustand';

const API_BASE_URL = import.meta.env.PROD
    ? '/api'
    : `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api`;

export interface Notification {
    id: string;
    _id?: string;
    userId: string;
    type: 'survey_response' | 'system' | 'promo';
    title: string;
    message: string;
    link?: string;
    read: boolean;
    createdAt: string;
}

interface NotificationState {
    notifications: Notification[];
    unreadCount: number;
    loading: boolean;
    pollInterval: ReturnType<typeof setInterval> | null;

    fetchNotifications: () => Promise<void>;
    markAsRead: (id: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    startPolling: () => void;
    stopPolling: () => void;
}

export const useNotificationStore = create<NotificationState>()((set, get) => ({
    notifications: [],
    unreadCount: 0,
    loading: false,
    pollInterval: null,

    fetchNotifications: async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/notifications`, {
                credentials: 'include',
            });
            if (!response.ok) return;
            const data = await response.json();
            set({
                notifications: (data.notifications || []).map((n: any) => ({
                    ...n,
                    id: n._id || n.id,
                })),
                unreadCount: data.unreadCount ?? 0,
            });
        } catch {
            // Silently fail - polling will retry
        }
    },

    markAsRead: async (id: string) => {
        try {
            await fetch(`${API_BASE_URL}/notifications/${id}/read`, {
                method: 'PUT',
                credentials: 'include',
            });
            set((s) => ({
                notifications: s.notifications.map((n) =>
                    n.id === id ? { ...n, read: true } : n
                ),
                unreadCount: Math.max(0, s.unreadCount - 1),
            }));
        } catch {
            // Silently fail
        }
    },

    markAllAsRead: async () => {
        try {
            await fetch(`${API_BASE_URL}/notifications/read-all`, {
                method: 'PUT',
                credentials: 'include',
            });
            set((s) => ({
                notifications: s.notifications.map((n) => ({ ...n, read: true })),
                unreadCount: 0,
            }));
        } catch {
            // Silently fail
        }
    },

    startPolling: () => {
        const { pollInterval } = get();
        if (pollInterval) return; // Already polling

        // Fetch immediately
        get().fetchNotifications();

        // Then poll every 30 seconds
        const interval = setInterval(() => {
            get().fetchNotifications();
        }, 30_000);

        set({ pollInterval: interval });
    },

    stopPolling: () => {
        const { pollInterval } = get();
        if (pollInterval) {
            clearInterval(pollInterval);
            set({ pollInterval: null });
        }
    },
}));
